// client/src/components/reviews/ReviewList.js
import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress, Button, MenuItem, Select } from '@mui/material';
import ReviewCard from './ReviewCard';
import ReviewForm from './ReviewForm';
import axiosInstance from '../../axiosInstance';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

/**
 * Props: collectionType, itemId, currentUserId, collectionItemId (if user owns the item)
 */
export default function ReviewList({ collectionType, itemId, currentUserId, collectionItemId }) {
  const { isFeatureEnabled } = useFeatureFlags();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [sort, setSort] = useState('createdAt');
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    if (!isFeatureEnabled('enableReviews')) return;
    if (!itemId) return;
    setLoading(true);
    axiosInstance.get(`/reviews/item/${collectionType}/${itemId}?page=${page}&sort=${sort}`)
      .then(res => {
        setReviews(page === 1 ? res.data.reviews : prev => [...prev, ...res.data.reviews]);
        setPages(res.data.pages);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [collectionType, itemId, page, sort]);

  const handleReviewCreated = (review) => {
    setReviews(prev => [review, ...prev]);
    setShowForm(false);
  };

  if (!isFeatureEnabled('enableReviews')) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">Reviews</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Select size="small" value={sort} onChange={e => { setSort(e.target.value); setPage(1); }}>
            <MenuItem value="createdAt">Newest</MenuItem>
            <MenuItem value="likes">Most liked</MenuItem>
          </Select>
          {collectionItemId && !showForm && (
            <Button variant="outlined" size="small" onClick={() => setShowForm(true)}>
              Write a review
            </Button>
          )}
        </Box>
      </Box>

      {showForm && (
        <ReviewForm
          collectionType={collectionType}
          collectionItemId={collectionItemId}
          onCreated={handleReviewCreated}
          onCancel={() => setShowForm(false)}
        />
      )}

      {loading && page === 1 ? (
        <CircularProgress size={24} />
      ) : reviews.length === 0 ? (
        <Typography color="text.secondary" variant="body2">No reviews yet. Be the first!</Typography>
      ) : (
        <>
          {reviews.map(r => (
            <ReviewCard key={r._id} review={r} currentUserId={currentUserId} />
          ))}
          {page < pages && (
            <Button onClick={() => setPage(p => p + 1)} disabled={loading}>Load more</Button>
          )}
        </>
      )}
    </Box>
  );
}
